import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  positionsDokumentHochladen,
  positionsDokumentVersionenLaden,
  produktionsbildErsetzen,
  produktionsbildHistorieLaden,
} from "../../../api/dokumente.ts";
import type { PositionsDokument, PositionsDokumenttyp, PositionsDokumentVersion, ProduktionsbildHistorienElement } from "../../../api/dokumente.ts";
import { geladeneDokumente, type PositionsDokumenteZustand } from "../../../lib/positionsDokumenteZustand.ts";
import { ladeUndSpeichereDatei } from "../../../api/basis.ts";
import { meldeFehler } from "../../../state/fehlerStapel.ts";
import { fehlertext } from "../../../lib/fehlertext.ts";
import {
  POSITION_BELEGT_TEXT,
  dateiZuGrossText,
  istZuGross,
  stehenderHinweisText,
  uploadFehlerText,
  type HinweisKontext,
} from "../../../lib/upload.ts";
import { Ikone } from "../../Ikone.tsx";
import { UploadAnsage, UploadFortschritt } from "../../Upload/UploadFortschritt.tsx";
import {
  uploadStelle,
  useUploadStelle,
  useUploadStellen,
  type UploadLauf,
  type UploadStellen,
} from "../../App/useUploadStellen.ts";
import type { ProduktionsbildOeffnen } from "../DocsTab/Positionsdokumente.tsx";

export interface PositionsDokumentMenueSchluessel {
  jtlPosId: number;
  docType: PositionsDokumenttyp;
}

export interface PositionsdokumentAktionenProps {
  bestellungId: string;
  /**
   * Die lesbare Bestellnummer (`orderNo`, sonst die Bestell-ID) und die Artikelnummer der Position
   * (`sku`) - beide nur fuer den stehenden Hinweis. Ein Upload, der scheitert, nachdem der Nutzer die
   * Bestellung gewechselt hat, hat in diesem Grid keine Anzeigeflaeche mehr; der Hinweis muss sagen,
   * zu welcher Bestellung und welchem Artikel er gehoert (Task 6, "Uploads bis 100 MB").
   */
  bestellNummer: string;
  artikelNummer: string | null;
  jtlPosId: number;
  /**
   * Die Positionsdokumente der Bestellung in drei Ladezustaenden (Fehleranzeige Paket 2, Task 13, Spec E4). Nur mit `geladen` zeigt die
   * Zelle ihre Knoepfe und kennt „vorhanden“/„nicht vorhanden“; sonst steht dort „nicht geladen“ (gescheitert) bzw. „Lädt…“ - ein
   * Hochladen auf unbekanntem Stand liesse den Nutzer eine vermeintlich erste Fassung anlegen. Der Upload-Ablauf selbst bleibt unberuehrt.
   */
  dokumenteZustand: PositionsDokumenteZustand;
  dokumenteNeuLaden?: () => Promise<void>;
  offenerSchluessel: PositionsDokumentMenueSchluessel | null;
  onOffenenSchluesselAendern: (schluessel: PositionsDokumentMenueSchluessel | null) => void;
  onProduktionsbildOeffnen?: ProduktionsbildOeffnen;
  /** `App.tsx#zeigeHinweis`: der Fehlschlag eines Uploads, dessen Kontext nicht mehr offen ist. */
  onStehenderHinweis?: (text: string) => void;
  /**
   * Der Store der Upload-Stellen (`App.tsx` ueber `BestellungTab` und `Positionen`, Spec 2026-10-20): die Sperre
   * dieser Position und die Fortschrittszeile leben dort, Schluessel `position:<bestellungId>:<jtlPosId>`, und
   * ueberleben Reiter- und Bestellungswechsel. Das Grid teilt sich die Sperre mit dem Abschnitt Positionsdokumente
   * im Reiter Dokumente (`DocsTab/Positionsdokumente.tsx`). Ohne Prop legt die Komponente sich einen eigenen an -
   * die Sperre gilt dann nur fuer diese Instanz (das Verhalten vor den Upload-Sperren).
   */
  uploadStellen?: UploadStellen;
}

type Historie = {
  docType: "artwork";
  versionen: PositionsDokumentVersion[];
} | {
  docType: "produktionsbilder";
  bilder: ProduktionsbildHistorienElement[];
};

interface AusstehenderErsatzBasis {
  bestellungId: string;
  jtlPosId: number;
  datei: File;
  rowVersion: number;
}

type AusstehenderErsatz = AusstehenderErsatzBasis & ({
  docType: "artwork";
} | {
  docType: "produktionsbilder";
  linkId: string;
});

interface HistorieKontext {
  bestellungId: string;
  jtlPosId: number;
  docType: PositionsDokumenttyp;
  nummer: number;
}

/**
 * Bestell- und Artikelnummer werden BEIM START des Uploads festgehalten, nicht beim Scheitern
 * gelesen: bis ein Upload von 100 MB scheitert, zeigt dieselbe Komponente womoeglich eine andere
 * Bestellung, und die Props waeren die der falschen.
 */
interface UploadKontext extends HistorieKontext {
  bestellNummer: string;
  artikelNummer: string | null;
}

const DOKUMENTTYPEN: readonly { docType: PositionsDokumenttyp; label: string; icon: "file-text" | "image" }[] = [
  { docType: "artwork", label: "Artwork", icon: "file-text" },
  { docType: "produktionsbilder", label: "Produktionsbilder", icon: "image" },
];

/** Bezeichnung, Bestell- und Artikelnummer eines Uploads, wie sie ein stehender Hinweis nennt (`lib/upload.ts#stehenderHinweisText`). */
function hinweisKontext(kontext: UploadKontext): HinweisKontext {
  const label = DOKUMENTTYPEN.find((eintrag) => eintrag.docType === kontext.docType)?.label ?? "Positionsdokument";
  return { label, bestellNummer: kontext.bestellNummer, artikelNummer: kontext.artikelNummer };
}

/**
 * Die Fehlermeldung des Alerts. `upload` ist gesetzt, wenn sie von einem gescheiterten Upload stammt (Kontext
 * vom Start); nur dann geht sie beim Schliessen des Menues als stehender Hinweis weiter. Alle anderen Fehler
 * (Download, Historie, Nachladen, zu grosse Datei vor dem Start) bleiben `null`.
 */
interface Fehlermeldung {
  text: string;
  upload: HinweisKontext | null;
}

export function PositionsdokumentAktionen({
  bestellungId,
  bestellNummer,
  artikelNummer,
  jtlPosId,
  dokumenteZustand,
  dokumenteNeuLaden,
  offenerSchluessel,
  onOffenenSchluesselAendern,
  onProduktionsbildOeffnen,
  onStehenderHinweis,
  uploadStellen,
}: PositionsdokumentAktionenProps) {
  const triggerRefs = useRef<Record<PositionsDokumenttyp, HTMLButtonElement | null>>({ artwork: null, produktionsbilder: null });
  const inputRefs = useRef<Record<PositionsDokumenttyp, HTMLInputElement | null>>({ artwork: null, produktionsbilder: null });
  const menuRef = useRef<HTMLDivElement | null>(null);
  const ersteAktionRef = useRef<HTMLButtonElement | null>(null);
  const historieNummer = useRef(0);
  const historieKontextRef = useRef<HistorieKontext | null>(null);
  const uploadNummer = useRef(0);
  const uploadKontextRef = useRef<UploadKontext | null>(null);
  const bestellungIdRef = useRef(bestellungId);
  const aktiverTypRef = useRef<PositionsDokumenttyp | null>(null);
  const vorherigerMenueSchluessel = useRef<string | null>(null);
  const menueInitialisiert = useRef(false);
  const vorherigeBestellungId = useRef(bestellungId);
  const schliessenNachUploadRef = useRef(false);
  const nachladeFehlerBewahren = useRef(false);
  const dialogRef = useRef<HTMLElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const historieSchliessenRef = useRef<HTMLButtonElement | null>(null);
  const [historie, setHistorie] = useState<Historie | null>(null);
  const [ausstehenderErsatz, setAusstehenderErsatz] = useState<AusstehenderErsatz | null>(null);
  const [notiz, setNotiz] = useState("");
  const [fehlerMeldung, setFehlerMeldung] = useState<Fehlermeldung | null>(null);
  const [menuePosition, setMenuePosition] = useState({ left: 8, top: 8 });

  // Die Sperre dieser Position und ihre Fortschrittszeile liegen im Store der App (Spec 2026-10-20), nicht in diesem
  // State: sie gehoeren zur Position DER BESTELLUNG, an der hochgeladen wird, und ueberleben Bestellungs- und
  // Reiterwechsel. `gesperrt` ist der Wert fuer den RENDER (Eingaben und "Ersatz hochladen" sind `disabled`); die
  // Handler fragen den Store SYNCHRON (`stellen.istGesperrt`, `versucheBeginn`), denn ein Render-Wert gilt erst
  // nach dem naechsten Zeichnen, und ein Ereignis darf die gesperrte Eingabe trotzdem erreichen.
  const lokaleStellen = useUploadStellen();
  const stellen = uploadStellen ?? lokaleStellen;
  const positionsStelle = uploadStelle.position(bestellungId, jtlPosId);
  const positionsStand = useUploadStelle(stellen, positionsStelle);
  const gesperrt = positionsStand.gesperrt;

  const aktiverTyp = offenerSchluessel?.jtlPosId === jtlPosId ? offenerSchluessel.docType : null;
  bestellungIdRef.current = bestellungId;
  aktiverTypRef.current = aktiverTyp;
  const fehler = fehlerMeldung?.text ?? null;
  // Die Meldung, wie sie in diesem Augenblick steht - fuer `ungueltigMachen`, das aus Effekten laeuft.
  const fehlerMeldungRef = useRef<Fehlermeldung | null>(null);
  fehlerMeldungRef.current = fehlerMeldung;
  // Der Rueckruf des LETZTEN Renders fuer den Abbau-Cleanup unten: der laeuft nur einmal (leeres Abhaengigkeitsarray).
  const onStehenderHinweisRef = useRef(onStehenderHinweis);
  onStehenderHinweisRef.current = onStehenderHinweis;
  const setFehler = (text: string | null, upload: HinweisKontext | null = null): void => {
    setFehlerMeldung(text === null ? null : { text, upload });
  };
  // Der Fortschritt gehoert zu genau einem Menue: Bestellung und Position folgen aus dem Schluessel der Stelle, den
  // Dokumenttyp traegt die Marke der Anzeige. Laeuft der Upload eines anderen Dokumenttyps dieser Position (oder
  // einer anderen Oberflaeche), soll das gerade offene Menue ihn nicht als seinen zeigen.
  const sichtbareUploadAnzeige = positionsStand.anzeige !== null && positionsStand.anzeige.marke === aktiverTyp
    ? positionsStand.anzeige
    : null;
  // Was die sichtbare Zeile zeigt, sagt auch die Region an (Menue und Ersatzdialog tragen je eine, nie beide zugleich).
  const ansageEintraege = sichtbareUploadAnzeige
    ? [{
      schluessel: positionsStelle,
      anzeige: { dateiname: sichtbareUploadAnzeige.dateiname, phase: "hochladen" as const, anteil: sichtbareUploadAnzeige.anteil },
    }]
    : [];

  const ungueltigMachen = (fehlerBewahren = false): void => {
    historieNummer.current += 1;
    historieKontextRef.current = null;
    uploadNummer.current += 1;
    uploadKontextRef.current = null;
    setHistorie(null);
    setAusstehenderErsatz(null);
    setNotiz("");
    if (!fehlerBewahren) {
      // Ein Upload-Fehler, den der Nutzer nicht selbst geschlossen hat, geht mit dem Menue nicht verloren:
      // der Alert sitzt unten links, oft ausserhalb des Drawers, und wird leicht uebersehen. Er geht als
      // stehender Hinweis weiter (Spec Z. 61-63). Die Ref wird SOFORT geleert: Menuewechsel und
      // Bestellungswechsel rufen `ungueltigMachen` im selben Commit zweimal, der Hinweis soll einmal kommen.
      const offen = fehlerMeldungRef.current;
      fehlerMeldungRef.current = null;
      if (offen?.upload) onStehenderHinweis?.(stehenderHinweisText(offen.upload, offen.text));
      setFehler(null);
    }
  };

  const menueKennung = aktiverTyp ? `${bestellungId}:${jtlPosId}:${aktiverTyp}` : null;
  useEffect(() => {
    if (menueInitialisiert.current && vorherigerMenueSchluessel.current !== menueKennung) {
      const fehlerBewahren = nachladeFehlerBewahren.current && menueKennung === null;
      const uploadSchliesstMenue = schliessenNachUploadRef.current && menueKennung === null;
      nachladeFehlerBewahren.current = false;
      schliessenNachUploadRef.current = false;
      if (!uploadSchliesstMenue) ungueltigMachen(fehlerBewahren);
    }
    vorherigerMenueSchluessel.current = menueKennung;
    menueInitialisiert.current = true;
  }, [menueKennung]);

  useEffect(() => {
    if (vorherigeBestellungId.current !== bestellungId) {
      if (aktiverTyp) onOffenenSchluesselAendern(null);
      ungueltigMachen();
    }
    vorherigeBestellungId.current = bestellungId;
  }, [aktiverTyp, bestellungId, onOffenenSchluesselAendern]);

  useEffect(() => {
    return () => {
      historieNummer.current += 1;
      uploadNummer.current += 1;
      // Wird die Zeile abgebaut, waehrend ihr Alert einen Upload-Fehler zeigt, geht er als stehender Hinweis weiter -
      // wie beim Schliessen des Menues (`ungueltigMachen`). Seit dem Key `pos-<jtlPosId>` (Upload-Sperren, Luecke 5)
      // wird die Zeile bei einem Bestellungswechsel mit anderen Positionen ABGEBAUT statt wiederverwendet, und kein
      // Effekt laeuft mehr. Ohne Mausklick (Browser-Zurueck, Tastatur) schliesst auch der mousedown-Handler das Menue
      // nicht vorher: der Alert verschwindet mit der Zeile, ohne dass der Nutzer ihn je gesehen haben muss. Die Ref
      // ist nach `ungueltigMachen` schon leer: der Hinweis kommt so genau einmal. Nur Upload-Fehler (`upload`) gehen
      // hinaus; Download-, Historie- und Nachladefehler bleiben bei der Zeile.
      const offen = fehlerMeldungRef.current;
      fehlerMeldungRef.current = null;
      if (offen?.upload) onStehenderHinweisRef.current?.(stehenderHinweisText(offen.upload, offen.text));
    };
  }, []);

  const aktualisiereMenuePosition = (): void => {
    if (!aktiverTyp) return;
    const trigger = triggerRefs.current[aktiverTyp];
    if (!trigger) return;
    const triggerRechteck = trigger.getBoundingClientRect();
    const menueRechteck = menuRef.current?.getBoundingClientRect();
    const breite = menueRechteck?.width || 260;
    const hoehe = menueRechteck?.height || 180;
    const rand = 8;
    const viewportBreite = window.innerWidth;
    const viewportHoehe = window.innerHeight;
    const links = Math.min(Math.max(rand, triggerRechteck.left), Math.max(rand, viewportBreite - breite - rand));
    const unterhalb = viewportHoehe - triggerRechteck.bottom;
    const oberhalb = triggerRechteck.top;
    const oben = unterhalb >= hoehe || unterhalb >= oberhalb
      ? triggerRechteck.bottom + 4
      : triggerRechteck.top - hoehe - 4;
    setMenuePosition({ left: links, top: Math.min(Math.max(rand, oben), Math.max(rand, viewportHoehe - hoehe - rand)) });
  };

  const modalAktiv = historie !== null || ausstehenderErsatz !== null;

  useLayoutEffect(() => {
    if (!aktiverTyp || modalAktiv) return;
    aktualisiereMenuePosition();
    ersteAktionRef.current?.focus();
  }, [aktiverTyp, modalAktiv]);

  useLayoutEffect(() => {
    if (!modalAktiv) return;
    if (ausstehenderErsatz) textareaRef.current?.focus();
    else historieSchliessenRef.current?.focus();
  }, [ausstehenderErsatz, historie, modalAktiv]);

  useEffect(() => {
    if (!modalAktiv) return;
    const fokusierbareElemente = (): HTMLElement[] => Array.from(
      dialogRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ) || [],
    );
    const fokusSichern = (event: FocusEvent): void => {
      if (dialogRef.current?.contains(event.target as Node)) return;
      fokusierbareElemente()[0]?.focus();
    };
    const beiTab = (event: KeyboardEvent): void => {
      if (event.key !== "Tab") return;
      const elemente = fokusierbareElemente();
      if (!elemente.length) return;
      const erstes = elemente[0];
      const letztes = elemente[elemente.length - 1];
      if (event.shiftKey && document.activeElement === erstes) {
        event.preventDefault();
        letztes.focus();
      } else if (!event.shiftKey && document.activeElement === letztes) {
        event.preventDefault();
        erstes.focus();
      }
    };
    document.addEventListener("focusin", fokusSichern, true);
    document.addEventListener("keydown", beiTab);
    return () => {
      document.removeEventListener("focusin", fokusSichern, true);
      document.removeEventListener("keydown", beiTab);
    };
  }, [modalAktiv]);

  useEffect(() => {
    if (!aktiverTyp) return;
    const beiVeraenderung = () => aktualisiereMenuePosition();
    window.addEventListener("resize", beiVeraenderung);
    document.addEventListener("scroll", beiVeraenderung, true);
    return () => {
      window.removeEventListener("resize", beiVeraenderung);
      document.removeEventListener("scroll", beiVeraenderung, true);
    };
  }, [aktiverTyp]);

  const menueSchliessenUndFokusWiederherstellen = (): void => {
    const ausloeser = aktiverTyp ? triggerRefs.current[aktiverTyp] : null;
    onOffenenSchluesselAendern(null);
    queueMicrotask(() => ausloeser?.focus());
  };

  useEffect(() => {
    if (!aktiverTyp) return;
    const beiPointerDown = (event: MouseEvent): void => {
      const ziel = event.target as Node;
      if (modalAktiv) return;
      if (
        menuRef.current?.contains(ziel)
        || triggerRefs.current[aktiverTyp]?.contains(ziel)
        || (ziel instanceof Element && ziel.closest(".board-editor-dialog"))
        // Die Meldung zu schliessen darf das Menue nicht mitschliessen: wer eine zu grosse Datei
        // gewaehlt hat, will danach eine andere waehlen.
        || (ziel instanceof Element && ziel.closest(".positionsdokument-grid-alert"))
      ) return;
      menueSchliessenUndFokusWiederherstellen();
    };
    const beiTaste = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      if (ausstehenderErsatz) {
        event.preventDefault();
        setAusstehenderErsatz(null);
      } else if (historie) {
        event.preventDefault();
        setHistorie(null);
      } else {
        event.preventDefault();
        menueSchliessenUndFokusWiederherstellen();
      }
    };
    document.addEventListener("mousedown", beiPointerDown, true);
    document.addEventListener("keydown", beiTaste);
    return () => {
      document.removeEventListener("mousedown", beiPointerDown, true);
      document.removeEventListener("keydown", beiTaste);
    };
  }, [aktiverTyp, ausstehenderErsatz, historie, modalAktiv, onOffenenSchluesselAendern]);

  // Knoepfe, Eingaben und Menue gibt es nur mit geladenem Stand (siehe unten); `dokumentFuer` wird nur dort befragt.
  const dokumenteGeladen = dokumenteZustand.art === "geladen";
  const dokumente = geladeneDokumente(dokumenteZustand);
  const dokumentFuer = (docType: PositionsDokumenttyp): PositionsDokument | undefined =>
    dokumente.find((eintrag) => eintrag.jtlPosId === jtlPosId && eintrag.docType === docType);

  const dateiOeffnen = async (fileId: string, originalName: string): Promise<void> => {
    setFehler(null);
    try {
      await ladeUndSpeichereDatei(`/api/files/${encodeURIComponent(fileId)}`, originalName);
    } catch (ursache) {
      // An den Stapel statt in den Portal-Alert (Spec R2: Aktionsfehler gehen an den Stapel); der feste
      // Satz wird zum Kontext, die Ursache kommt aus `fehlertext()`.
      meldeFehler(ursache, "Die Datei konnte nicht heruntergeladen werden.");
    }
  };

  const historieIstAktuell = (kontext: HistorieKontext): boolean => {
    const aktuell = historieKontextRef.current;
    return historieNummer.current === kontext.nummer
      && bestellungIdRef.current === kontext.bestellungId
      && aktiverTypRef.current === kontext.docType
      && aktuell?.bestellungId === kontext.bestellungId
      && aktuell.jtlPosId === kontext.jtlPosId
      && aktuell.docType === kontext.docType;
  };

  const uploadIstAktuell = (kontext: UploadKontext): boolean => {
    const aktuell = uploadKontextRef.current;
    return uploadNummer.current === kontext.nummer
      && bestellungIdRef.current === kontext.bestellungId
      && aktuell?.bestellungId === kontext.bestellungId
      && aktuell.jtlPosId === kontext.jtlPosId
      && aktuell.docType === kontext.docType;
  };

  const historieLaden = async (docType: PositionsDokumenttyp): Promise<void> => {
    const kontext: HistorieKontext = { bestellungId, jtlPosId, docType, nummer: ++historieNummer.current };
    historieKontextRef.current = kontext;
    setFehler(null);
    try {
      if (docType === "produktionsbilder") {
        const bilder = await produktionsbildHistorieLaden(bestellungId, jtlPosId);
        if (historieIstAktuell(kontext)) setHistorie({ docType, bilder });
        return;
      }
      const versionen = await positionsDokumentVersionenLaden(bestellungId, jtlPosId, docType);
      if (historieIstAktuell(kontext)) setHistorie({ docType, versionen });
    } catch (ursache) {
      // Inline bleibt inline (Spec R2): der Portal-Alert steht schon heute neben dem Menue. Neu ist der Text -
      // der feste Satz wird zum Kontext, die Ursache (Netz, 413, ...) kommt aus `fehlertext()` (R1).
      if (historieIstAktuell(kontext)) setFehler(fehlertext(ursache, "Die Versionshistorie konnte nicht geladen werden."));
    }
  };

  /**
   * Ein gescheiterter Upload. Ist sein Kontext noch offen, steht der Fehler im Alert dieses Grids.
   * Ist er es nicht mehr (Bestellung gewechselt, Menue geschlossen, Zeile verschwunden), gaebe es
   * keine Stelle mehr, an der der Alert zum Upload passt - frueher wurde hier geschwiegen, und ein
   * Upload von 100 MB, der nach Minuten scheitert, ging verloren, ohne dass jemand es erfuhr. Jetzt
   * geht er als stehender Hinweis mit Bestell- und Artikelnummer an die App.
   */
  const uploadFehlerMelden = (kontext: UploadKontext, ursache: unknown, datei: File): void => {
    const text = uploadFehlerText(ursache, datei);
    if (uploadIstAktuell(kontext)) setFehler(text, hinweisKontext(kontext));
    else onStehenderHinweis?.(stehenderHinweisText(hinweisKontext(kontext), text));
  };

  /**
   * Ein erfolgreicher Upload (Abschluss-Fix 1, Zweig-Review I-2). Das Dokument IST gespeichert, also muss das Grid
   * es nachladen - gleich ob das Menue, aus dem der Upload kam, noch offen ist. Ein Upload von 80 MB laeuft Minuten;
   * wer inzwischen daneben klickt, den Reiter wechselt oder den Drawer schliesst, sah sonst weiter "nicht vorhanden"
   * und lud ein zweites Mal hoch (409 bzw. eine zweite Produktionsbild-Version).
   *
   * Nur was die offene Instanz betrifft - Menue schliessen, Fehler zeigen, Nachlade-Fehler bewahren - bleibt an
   * `uploadIstAktuell` gebunden. `dokumenteNeuLaden` selbst ist es nicht: der Rueckruf der App
   * (`App.tsx#positionsDokumenteNeuLaden`) laedt immer den Drawer, der JETZT offen ist, verwirft veraltete Antworten
   * ueber seine Ladefolge und faengt Fehler selbst - er ist nach Menueschluss, Reiterwechsel, Bestellungswechsel und
   * Abbau gefahrlos. Scheitert er trotzdem, gibt es ohne offene Instanz keine Flaeche fuer eine Meldung; die App meldet
   * in ihrem eigenen Rueckruf (R-M16) "Die Änderung ist gespeichert, die Positionsdokumente konnten aber nicht aktualisiert
   * werden. Bitte neu laden." - ohne Wiederholungsrat, denn das Dokument ist gespeichert. Dieser Rueckruf lehnt deshalb
   * heute nie ab: das `catch` unten faengt nur einen kuenftigen Rueckruf, der ablehnt.
   *
   * Der Lauf des Uploads endet erst NACH diesem Nachladen (Aufrufer: `erstversionHochladen`, `ersatzHochladen`,
   * `finally`): bis dahin bliebe sonst eine neue Instanz "frei, aber alter Stand", und ein schneller zweiter Upload
   * liefe mit veralteter `rowVersion` in den 409.
   */
  const uploadErfolgNachladen = async (kontext: UploadKontext): Promise<void> => {
    if (uploadIstAktuell(kontext)) {
      nachladeFehlerBewahren.current = true;
      schliessenNachUploadRef.current = true;
      onOffenenSchluesselAendern(null);
    }
    try {
      await dokumenteNeuLaden?.();
    } catch {
      // Ein Kontext, der schon vor dem Nachladen veraltet war, wird nie wieder aktuell (die Nummer zaehlt nur hoch).
      if (uploadIstAktuell(kontext)) setFehler("Das Positionsdokument wurde hochgeladen, aber die Anzeige konnte nicht aktualisiert werden.");
    }
  };

  /**
   * Die Fortschrittszeile des Laufs: Dateiname, Phase "hochladen" und der Anteil, mit dem Dokumenttyp als Marke -
   * das Menue zeigt sie nur beim Typ, der hochlaedt (`sichtbareUploadAnzeige`).
   */
  const fortschrittZeigen = (lauf: UploadLauf, datei: File, docType: PositionsDokumenttyp, anteil: number): void => {
    lauf.zeige({ dateiname: datei.name, phase: "hochladen", anteil, marke: docType });
  };

  const erstversionHochladen = async (docType: PositionsDokumenttyp, datei: File): Promise<void> => {
    // Exklusiv und synchron: ein zweiter Upload an dieser Position beginnt nicht, auch nicht vor dem naechsten Zeichnen.
    // Vor `++uploadNummer`: ein abgewiesener Versuch darf den Kontext des laufenden Uploads nicht ungueltig machen.
    // `onChange` prueft die Sperre schon vor `dateiGewaehlt` und meldet sie selbst; dieser Zweig ist ein Schutz
    // dahinter, damit ein Start auch hier nie still ins Leere laeuft.
    const lauf = stellen.versucheBeginn(positionsStelle);
    if (lauf === null) {
      setFehler(POSITION_BELEGT_TEXT);
      return;
    }
    const kontext: UploadKontext = {
      bestellungId, bestellNummer, artikelNummer, jtlPosId, docType, nummer: ++uploadNummer.current,
    };
    uploadKontextRef.current = kontext;
    setFehler(null);
    fortschrittZeigen(lauf, datei, docType, 0);
    try {
      let erfolgreich = false;
      try {
        await positionsDokumentHochladen(
          bestellungId, jtlPosId, docType, datei, null, undefined, (anteil) => fortschrittZeigen(lauf, datei, docType, anteil),
        );
        erfolgreich = true;
      } catch (ursache) {
        uploadFehlerMelden(kontext, ursache, datei);
      }
      if (erfolgreich) await uploadErfolgNachladen(kontext);
    } finally {
      // Erst nach dem Nachladen (siehe `uploadErfolgNachladen`), auch nach einem Fehler.
      lauf.beende();
    }
  };

  const dateiGewaehlt = (docType: PositionsDokumenttyp, datei: File): void => {
    // Vorabpruefung: spart den Upload, der doch nur mit 413 endete, und den Ersatzdialog samt
    // Begruendung fuer eine Datei, die ohnehin nicht hochgeladen wird.
    if (istZuGross(datei)) {
      setFehler(dateiZuGrossText(datei));
      return;
    }
    const dokument = dokumentFuer(docType);
    if (!dokument) {
      void erstversionHochladen(docType, datei);
      return;
    }
    historieNummer.current += 1;
    historieKontextRef.current = null;
    setHistorie(null);
    setFehler(null);
    setNotiz("");
    setAusstehenderErsatz(docType === "produktionsbilder"
      ? { bestellungId, jtlPosId, docType, datei, rowVersion: dokument.rowVersion, linkId: dokument.linkId }
      : { bestellungId, jtlPosId, docType, datei, rowVersion: dokument.rowVersion },
    );
  };

  const ersatzHochladen = async (): Promise<void> => {
    if (!ausstehenderErsatz || notiz.trim().length < 3) return;
    const ersatz = ausstehenderErsatz;
    // Exklusiv und synchron, an der Position DES ERSATZES (festgehalten bei der Auswahl). Ist sie belegt - ein anderer
    // Upload (etwa im Reiter Dokumente begonnen) hat sie inzwischen genommen -, startet nichts. Das darf nicht still
    // geschehen: eine Meldung, der Dialog bleibt offen und behaelt Datei und Begruendung. Normalerweise kommt ein Klick
    // gar nicht an, denn die Schaltflaeche ist bei belegter Position `disabled`.
    const lauf = stellen.versucheBeginn(uploadStelle.position(ersatz.bestellungId, ersatz.jtlPosId));
    if (lauf === null) {
      setFehler(POSITION_BELEGT_TEXT);
      return;
    }
    const kontext: UploadKontext = {
      bestellungId: ersatz.bestellungId,
      bestellNummer,
      artikelNummer,
      jtlPosId: ersatz.jtlPosId,
      docType: ersatz.docType,
      nummer: ++uploadNummer.current,
    };
    uploadKontextRef.current = kontext;
    setFehler(null);
    fortschrittZeigen(lauf, ersatz.datei, ersatz.docType, 0);
    const fortschritt = (anteil: number): void => fortschrittZeigen(lauf, ersatz.datei, ersatz.docType, anteil);
    try {
      let erfolgreich = false;
      try {
        if (ersatz.docType === "produktionsbilder") {
          await produktionsbildErsetzen(
            ersatz.bestellungId,
            ersatz.jtlPosId,
            ersatz.linkId,
            ersatz.datei,
            ersatz.rowVersion,
            notiz.trim(),
            fortschritt,
          );
        } else {
          await positionsDokumentHochladen(
            ersatz.bestellungId,
            ersatz.jtlPosId,
            ersatz.docType,
            ersatz.datei,
            ersatz.rowVersion,
            notiz.trim(),
            fortschritt,
          );
        }
        erfolgreich = true;
      } catch (ursache) {
        uploadFehlerMelden(kontext, ursache, ersatz.datei);
      }
      if (!erfolgreich) return;
      if (uploadIstAktuell(kontext)) {
        setAusstehenderErsatz(null);
        setNotiz("");
      }
      await uploadErfolgNachladen(kontext);
    } finally {
      // Erst nach dem Nachladen (siehe `uploadErfolgNachladen`), auch nach einem Fehler.
      lauf.beende();
    }
  };

  const aktivesDokument = aktiverTyp ? dokumentFuer(aktiverTyp) : undefined;
  const aktiveDefinition = aktiverTyp ? DOKUMENTTYPEN.find((eintrag) => eintrag.docType === aktiverTyp) : undefined;

  return (
    <div className="item-document-actions">
      {!dokumenteGeladen ? (
        dokumenteZustand.art === "gescheitert" ? (
          <span className="item-document-status" title="Die Positionsdokumente konnten nicht geladen werden – siehe Reiter Dokumente.">nicht geladen</span>
        ) : (
          <span className="item-document-status">Lädt…</span>
        )
      ) : DOKUMENTTYPEN.map(({ docType, label, icon }) => {
        const dokument = dokumentFuer(docType);
        const schluessel = { jtlPosId, docType } satisfies PositionsDokumentMenueSchluessel;
        const menueOffen = offenerSchluessel?.jtlPosId === jtlPosId && offenerSchluessel.docType === docType;
        return (
          <span key={docType}>
            <button
              ref={(element) => { triggerRefs.current[docType] = element; }}
              type="button"
              className={`item-document-action ${dokument ? "item-document-action--vorhanden" : "item-document-action--fehlend"}`}
              aria-label={`${label} ${dokument ? "vorhanden" : "nicht vorhanden"}`}
              aria-expanded={menueOffen}
              onClick={() => onOffenenSchluesselAendern(menueOffen ? null : schluessel)}
            >
              <Ikone name={icon} />
            </button>
            <input
              ref={(element) => { inputRefs.current[docType] = element; }}
              type="file"
              aria-label={`${label} hochladen`}
              className="visuell-versteckt"
              disabled={gesperrt}
              onChange={(event) => {
                const datei = event.currentTarget.files?.[0];
                event.currentTarget.value = "";
                if (!datei) return;
                // Synchron am Store gefragt: zwei Ereignisse vor dem naechsten Zeichnen sehen beide den Render-Wert "frei".
                // Die Eingabe ist bei belegter Position `disabled`; erreicht eine Auswahl sie trotzdem (die Sperre kam
                // zwischen dem letzten Zeichnen und dem Ereignis), wird die Datei nicht still verworfen: die Meldung sagt,
                // warum nichts geschieht. Kein Ersatzdialog und kein Start an einer belegten Position.
                if (stellen.istGesperrt(positionsStelle)) {
                  setFehler(POSITION_BELEGT_TEXT);
                  return;
                }
                dateiGewaehlt(docType, datei);
              }}
            />
          </span>
        );
      })}

      {dokumenteGeladen && aktiverTyp && aktiveDefinition && !modalAktiv ? createPortal(
        <div
          ref={menuRef}
          className="positionsdokument-grid-menu"
          role="dialog"
          aria-label={`${aktiveDefinition.label} Aktionen`}
          style={{ left: menuePosition.left, top: menuePosition.top }}
        >
          {aktivesDokument ? (
            <>
              <div className="positionsdokument-grid-menu-head">
                <strong>{aktivesDokument.originalName}</strong>
                <span>{`Version ${aktivesDokument.version}`}</span>
              </div>
              <button ref={ersteAktionRef} type="button" onClick={(event) => {
                if (aktiverTyp === "produktionsbilder" && onProduktionsbildOeffnen) {
                  const ausloeser = triggerRefs.current[aktiverTyp] ?? event.currentTarget;
                  onOffenenSchluesselAendern(null);
                  onProduktionsbildOeffnen(aktivesDokument.linkId, ausloeser);
                  return;
                }
                void dateiOeffnen(aktivesDokument.fileId, aktivesDokument.originalName);
              }}>Öffnen</button>
              <button type="button" onClick={() => inputRefs.current[aktiverTyp]?.click()}>Neue Version hochladen</button>
              <div className="positionsdokument-grid-menu-separator" />
              <button type="button" onClick={() => void historieLaden(aktiverTyp)}>Historie anzeigen</button>
            </>
          ) : (
            <>
              <div className="positionsdokument-grid-menu-head"><strong>Noch nicht vorhanden</strong></div>
              <button ref={ersteAktionRef} type="button" onClick={() => inputRefs.current[aktiverTyp]?.click()}>Hochladen</button>
            </>
          )}
          {/* Die Region steht, solange das Menue offen ist - der Nutzer oeffnet es vor dem Upload und es bleibt beim Start offen. */}
          <UploadAnsage anzeigen={ansageEintraege} />
          {sichtbareUploadAnzeige ? (
            <UploadFortschritt dateiname={sichtbareUploadAnzeige.dateiname} phase="hochladen" anteil={sichtbareUploadAnzeige.anteil} />
          ) : null}
        </div>,
        document.body,
      ) : null}

      {historie && aktiverTyp ? createPortal(
        <div className="board-editor-overlay open positionsdokument-modal-overlay" role="presentation">
          <section ref={dialogRef} className="board-editor-dialog overwrite-dialog" role="dialog" aria-modal="true" aria-label={`${DOKUMENTTYPEN.find((eintrag) => eintrag.docType === historie.docType)?.label} Versionshistorie`}>
            <div className="board-editor-head"><h2>Versionshistorie</h2></div>
            <div className="board-editor-body">
              {historie.docType === "artwork" ? (
                historie.versionen.length ? historie.versionen.map((version) => (
                  <div className="positionsdokument-historie-zeile" key={`${version.version}-${version.fileId}`}>
                    <span>{`Version ${version.version}`}</span>
                    <button type="button" onClick={() => void dateiOeffnen(version.fileId, version.originalName)}>Öffnen</button>
                  </div>
                )) : <p>Keine älteren Fassungen vorhanden.</p>
              ) : (
                historie.bilder.length ? historie.bilder.map((bild) => (
                  <div className="positionsdokument-historie-zeile" key={bild.linkId}>
                    <strong>{bild.originalName}</strong>
                    <span>{bild.deletedAt === null ? "Aktiv" : `Gelöscht am ${new Date(bild.deletedAt).toLocaleString()}`}</span>
                    {bild.versionen.map((version) => (
                      <div key={`${version.version}-${version.fileId}`}>
                        <span>{`Version ${version.version}`}</span>
                        <button type="button" onClick={() => void dateiOeffnen(version.fileId, version.originalName)}>Öffnen</button>
                      </div>
                    ))}
                  </div>
                )) : <p>Keine Produktionsbilder vorhanden.</p>
              )}
            </div>
            <div className="board-editor-foot"><button ref={historieSchliessenRef} className="btn-sec" type="button" onClick={() => setHistorie(null)}>Schließen</button></div>
          </section>
        </div>, document.body,
      ) : null}

      {ausstehenderErsatz ? createPortal(
        <div className="board-editor-overlay open positionsdokument-modal-overlay" role="presentation">
          <section ref={dialogRef} className="board-editor-dialog overwrite-dialog" role="dialog" aria-modal="true" aria-label={`${DOKUMENTTYPEN.find((eintrag) => eintrag.docType === ausstehenderErsatz.docType)?.label} ersetzen`}>
            <div className="board-editor-head"><h2>{`${DOKUMENTTYPEN.find((eintrag) => eintrag.docType === ausstehenderErsatz.docType)?.label} ersetzen`}</h2></div>
            <div className="board-editor-body overwrite-body">
              <label className="overwrite-note">
                Begründung für das Überschreiben <span>Pflichtfeld</span>
                <textarea ref={textareaRef} minLength={3} value={notiz} onChange={(event) => setNotiz(event.currentTarget.value)} />
              </label>
              {/* Innerhalb des aria-modal-Dialogs: eine Region ausserhalb blendet Hilfstechnik mit aus. */}
              <UploadAnsage anzeigen={ansageEintraege} />
              {sichtbareUploadAnzeige ? (
                <UploadFortschritt dateiname={sichtbareUploadAnzeige.dateiname} phase="hochladen" anteil={sichtbareUploadAnzeige.anteil} />
              ) : null}
            </div>
            <div className="board-editor-foot">
              <button className="btn-sec" type="button" onClick={() => setAusstehenderErsatz(null)}>Abbrechen</button>
              <button className="btn-primary" type="button" disabled={notiz.trim().length < 3 || gesperrt} onClick={() => void ersatzHochladen()}>Ersatz hochladen</button>
            </div>
          </section>
        </div>, document.body,
      ) : null}

      {fehler ? createPortal(
        <div className="positionsdokument-grid-alert" role="alert">
          <span>{fehler}</span>
          <button type="button" className="icon-btn" aria-label="Meldung schließen" onClick={() => setFehler(null)}>
            <Ikone name="x" />
          </button>
        </div>,
        document.body,
      ) : null}
    </div>
  );
}
